/**
 * AdminKeyGate — the unlock screen in front of the whole operator console.
 *
 * Every /api/admin route now requires the `x-admin-key` header. Without this
 * screen the operator would land on a dashboard where every tab reported
 * "Unauthorized", so the key is asked for once here and reused by every call.
 * It is held in sessionStorage for this browser tab only and is never part of
 * the build output.
 */
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import { verifyAdminKey } from '../../services/adminService';
import { setAdminKey } from '../../services/adminKey';
import { VALET, FONTS, TYPE } from '../../theme';

const Screen = styled(Box)({
    minHeight: '100vh',
    backgroundColor: VALET.bgMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    '@media (max-width: 768px)': {
        // Top-aligned rather than centred: once the phone keyboard opens, a
        // vertically centred panel jumps and can push the Unlock button off.
        alignItems: 'flex-start',
        padding: '32px 16px',
    },
});

const Panel = styled(Box)({
    width: '100%',
    maxWidth: 420,
    backgroundColor: VALET.bg,
    border: `1px solid ${VALET.border}`,
    borderRadius: 10,
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (max-width: 768px)': {
        padding: '24px 20px',
    },
});

const LogoBox = styled(Box)({
    width: 34,
    height: 34,
    borderRadius: 8,
    background: VALET.green,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONTS.display,
    fontSize: 17,
    fontWeight: 600,
    letterSpacing: '-0.02em',
});

const Title = styled(Typography)({
    ...TYPE.display,
    fontSize: '22px',
    color: VALET.ink,
});

const Help = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textSecondary,
    lineHeight: 1.5,
});

const ErrorText = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    fontWeight: 500,
    color: VALET.danger,
});

export default function AdminKeyGate({ onUnlocked }) {
    const [draft, setDraft] = useState('');
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        const key = draft.trim();
        if (!key || checking) return;
        setChecking(true);
        setError(null);
        try {
            // Check the key against the API before storing it, so a typo says so
            // here instead of failing on whichever tab is opened first.
            await verifyAdminKey(key);
            setAdminKey(key);
            setDraft('');
            onUnlocked(key);
        } catch (err) {
            setError(err.message || 'That admin key was not accepted.');
        } finally {
            setChecking(false);
        }
    };

    return (
        <Screen>
            <Panel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <LogoBox>V</LogoBox>
                    <Title>Operator console</Title>
                </Box>
                <Help>
                    This console shows customer and valet records, so it asks for the admin
                    key. It is kept for this browser tab only — you will need it again after
                    you close the tab.
                </Help>
                <TextField
                    type="password"
                    label="Admin key"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                    autoFocus
                    size="small"
                    fullWidth
                    disabled={checking}
                />
                {error && <ErrorText>{error}</ErrorText>}
                <Box>
                    <Button
                        variant="contained"
                        onClick={submit}
                        disabled={!draft.trim() || checking}
                        startIcon={
                            checking ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null
                        }
                        sx={{
                            background: VALET.green,
                            borderRadius: 0,
                            textTransform: 'none',
                            '&:hover': { background: VALET.greenDark },
                            // A thumb-sized target on a phone, where this is the
                            // only thing on the screen to press.
                            '@media (max-width: 768px)': {
                                width: '100%',
                                padding: '12px',
                                fontSize: '15px',
                            },
                        }}
                    >
                        {checking ? 'Checking…' : 'Unlock'}
                    </Button>
                </Box>
            </Panel>
        </Screen>
    );
}
